import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Button, CircularProgress, Pagination, Chip
} from '@mui/material';
import { Add } from '@mui/icons-material';
import DynamicTable from '../Dynamic/DynamicTable';
import DynamicModal from '../Dynamic/DynamicModal';
import FarmerForm from './FarmerForm'; // Import the new form
import '../Production/Production.css'; // Reusing the existing CSS
import { GetApi, DeleteApi } from "../../Services/api/ApiCall";
import API_URLS from '../../Services/api/action';
import { useUser } from "../../Context/UserContext";

const Farmer = () => {
  const [openForm, setOpenForm] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState('create');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const { token, logout } = useUser();

  // Fetch Data
  const fetchData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      // Assuming API_URLS.Farmers is defined as '/users/farmers/'
      const farmersUrl = `${API_URLS.Farmers}?page=${page}`;
      
      const farmersRes = await GetApi(farmersUrl, token);

      if (farmersRes?.results) {
        setFarmers(farmersRes.results);
        setTotalCount(farmersRes.count || 0);
        setTotalPages(Math.ceil((farmersRes.count || 0) / 10));
      } else {
        setFarmers([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching farmers:', error);
      setFarmers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData(currentPage);
  }, [fetchData, currentPage]);

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await DeleteApi(API_URLS.Farmers, { id: deleteId }, token);
      fetchData(currentPage);
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete farmer.');
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const handleEdit = (farmer) => {
    setSelectedFarmer(farmer);
    setActionType('edit');
    setOpenForm(true);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchData(1);
    setOpenForm(false);
    setSelectedFarmer(null);
    setActionType('create');
  };

  if (loading && farmers.length === 0) {
    return (
      <Box className="dashboard-container" display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      {/* Main Content */}
      <Box className="dashboard-container" sx={{ pt: 3, pb: 4 }}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                Farmer Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage registered farmers ({totalCount} total)
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setActionType('create');
                setSelectedFarmer(null);
                setOpenForm(true);
              }}
            >
              New Farmer
            </Button>
          </Box>

          {/* Dynamic Table */}
          <DynamicTable
            data={farmers}
            columns={[
              { 
                field: 'farmer_id', 
                headerName: 'Farmer ID',
                width: 120
              },
              { 
                field: 'name', 
                headerName: 'Name',
                width: 200
              },
              { 
                field: 'mobile_number', 
                headerName: 'Mobile',
                width: 130
              },
              { 
                field: 'village', 
                headerName: 'Village',
                width: 150
              },
              { 
                field: 'land_panel_details', 
                headerName: 'Land Details',
                width: 250,
                render: (row) => (
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 250 }}>
                    {row.land_panel_details || 'N/A'}
                  </Typography>
                )
              },
              { 
                field: 'number_of_saplings_request', 
                headerName: 'Saplings',
                width: 100,
                render: (row) => row.number_of_saplings_request?.toLocaleString() || '0'
              },
              { 
                field: 'species_preference', 
                headerName: 'Species',
                width: 200,
                render: (row) => (
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                    {row.species_preference || 'N/A'}
                  </Typography>
                )
              }
            ]}
            onEdit={handleEdit}
            onDelete={(row) => {
              setDeleteId(row.id);
              setDeleteConfirmOpen(true);
            }}
            loading={loading}
            emptyMessage="No farmers found"
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={6} mb={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}

          {/* Farmer Form Drawer */}
          <FarmerForm 
            open={openForm}
            onClose={() => {
              setOpenForm(false);
              setSelectedFarmer(null);
              setActionType('create');
            }}
            selectedFarmer={selectedFarmer}
            actionType={actionType}
            onSuccess={handleRefresh}
          />

          {/* Delete Confirmation Modal */}
          <DynamicModal
            open={deleteConfirmOpen}
            title="Confirm Delete"
            contentText="Are you sure you want to delete this farmer? This action cannot be undone."
            onClose={() => setDeleteConfirmOpen(false)}
            onConfirm={handleDeleteConfirm}
            confirmText="Delete"
            cancelText="Cancel"
            loading={loading}
          />
        </Container>
      </Box>
    </>
  );
};

export default Farmer;